import { Application, Container, Graphics } from 'pixi.js';
import { dayPhase, daylight, type DuckColor } from '@duck/contracts';
import { isReducedMotion } from '../office/render/reduced-motion';

/**
 * M0 "First Breath" — a tiny, self-contained LIVING backdrop for the pre-auth screens (Login /
 * Register / Join). The whole point of M0 is that a newcomer feels the world *breathe* before being
 * asked for anything: there must be NO blank, static, or "Loading…" first frame. So even before a
 * person signs in, the world already moves behind the glass card — a warm sky that drifts with the
 * time of day, a few ambient ducks gliding across a meadow, and soft floating motes.
 *
 * This is intentionally DECOUPLED from the office engine and the realtime client: it runs pre-auth,
 * with no server, no socket, no @duck/contracts world state. It only borrows the SHARED, pure
 * `daylight()`/`dayPhase()` curve so the pre-auth sky and the in-world sky share one visual language.
 *
 * PERFORMANCE (the M4.2 discipline, restated): everything is OBJECT-POOLED and allocation-free on the
 * hot path. The duck count is hard-capped ({@link AMBIENT_DUCKS}), motes are hard-capped
 * ({@link MOTES}), and the sky is a single tinted Graphics (no per-frame redraw). We `new` nothing in
 * {@link tick}. The canvas is capped at a modest resolution (this is a backdrop, not the gameplay
 * surface) so it never competes with the auth form for GPU.
 *
 * ACCESSIBILITY: this layer is purely DECORATIVE and sits BEHIND the auth card (which has its own
 * solid/blurred background), so it never reduces the legibility of the form. The mount element is
 * `aria-hidden` and `pointer-events: none`. Under `prefers-reduced-motion` we render a single correct,
 * STATIC frame (a calm meadow at its current time of day) and then stop all motion — no gliding, no
 * drifting, no bobbing.
 */

/** A self-contained palette so the backdrop needs nothing from the office render layer. Mirrors the full
 *  16-tint roster (Art revision 2026-06-22) so ambient ducks can drift in any of the new colours too. */
const DUCK_HEX: Record<DuckColor, number> = {
  yellow: 0xffd23f,
  white: 0xf4f4f8,
  brown: 0xb5793a,
  green: 0x6fcf5f,
  blue: 0x5aa9e6,
  pink: 0xff9ecb,
  black: 0x3a3a44,
  orange: 0xff924c,
  red: 0xed5a5a,
  peach: 0xffc6a3,
  lavender: 0xc6b6f0,
  purple: 0xa977e0,
  teal: 0x3fc7c2,
  mint: 0x9be3c0,
  cream: 0xf5e6c8,
  grey: 0x9aa3b0,
};
const BEAK = 0xff9f1c;

/** Hard cap on ambient ducks gliding across the meadow. A few is plenty to make it feel alive. */
const AMBIENT_DUCKS = 5;
/** Hard cap on floating motes (dust/pollen catching the light). Pooled, reused forever. */
const MOTES = 26;

/** Warm dawn/dusk wash vs. cool night wash — mixed by phase so the sky *feels* the time of day. */
const WARM = { r: 1.0, g: 0.66, b: 0.38 };
const COOL = { r: 0.28, g: 0.36, b: 0.7 };
/** Floor on darkness so the meadow silhouette + motes stay readable even at midnight (a11y). */
const MIN_DAYLIGHT = 0.45;

interface AmbientDuck {
  gfx: Container;
  x: number;
  y: number;
  /** Horizontal glide speed (px/s); the ducks wander left↔right and wrap. */
  speed: number;
  dir: 1 | -1;
  bobPhase: number;
  bobAmp: number;
  baseY: number;
}

interface Mote {
  gfx: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  phase: number;
}

export interface LivingBackdrop {
  /** Tears down the Pixi app + pools. Idempotent. */
  destroy(): void;
}

/**
 * Mount the living backdrop into `container`. Resolves once Pixi is initialised and the FIRST frame is
 * already on screen (so the caller can trust the world is moving). The returned handle's `destroy()`
 * fully tears down the app and clears the dev observability flag.
 *
 * Returns `null` (gracefully) if Pixi can't initialise (e.g. no WebGL) — the auth screen still works,
 * it just doesn't get the animated backdrop.
 */
export async function mountLivingBackdrop(container: HTMLElement): Promise<LivingBackdrop | null> {
  const app = new Application();
  let destroyed = false;

  try {
    await app.init({
      resizeTo: container,
      antialias: true,
      backgroundAlpha: 0,
      // A backdrop never needs retina sharpness; cap DPR so it stays cheap behind the form.
      resolution: Math.min(window.devicePixelRatio || 1, 1.5),
      autoDensity: true,
    });
  } catch {
    app.destroy(true);
    return null;
  }

  // The caller may have torn the screen down while we were awaiting init.
  if (!container.isConnected) {
    app.destroy(true);
    return null;
  }

  container.appendChild(app.canvas);

  const scene = new Scene(app);
  app.ticker.add(scene.tick);
  // perf cost-census: this pre-auth backdrop is decorative drift — cap it at 30 FPS (it doesn't need 60,
  // and it runs a full second WebGL loop while the user reads the login/join card).
  app.ticker.maxFPS = 30;

  // Dev-only observability for the e2e "manual testing agent": a tiny, product-irrelevant flag the
  // test can poll to assert "the world is moving before any input". Mirrors `__duckWorld`/`__duckVoice`.
  if (typeof window !== 'undefined' && import.meta.env?.DEV) {
    (window as unknown as { __duckFirstBreath?: { backdropAlive: boolean } }).__duckFirstBreath = {
      backdropAlive: true,
    };
  }

  return {
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      app.ticker.remove(scene.tick);
      scene.destroy();
      app.destroy(true, { children: true });
      if (typeof window !== 'undefined' && import.meta.env?.DEV) {
        delete (window as unknown as { __duckFirstBreath?: unknown }).__duckFirstBreath;
      }
    },
  };
}

/** The pooled, allocation-free scene. One sky Graphics, a meadow, capped ducks + motes. */
class Scene {
  private readonly root = new Container();
  private readonly sky: Graphics;
  private readonly meadow: Graphics;
  private readonly ducks: AmbientDuck[] = [];
  private readonly motes: Mote[] = [];

  /** Real wall-clock time-of-day so the pre-auth sky matches "now" (Wonder = the world is real). */
  private clock = 0;
  /** Eased sky wash (mutated in place — never reallocated). */
  private readonly light = { r: 0.5, g: 0.5, b: 0.7 };
  private firstFrame = true;
  /**
   * Under reduced motion we compose ONE correct static frame and then stop all per-frame work — on the
   * very low-end / accessibility device this pre-auth layer is meant to protect, re-writing identical
   * duck/mote/sky props ~60×/s would force a continuous full-stage redraw for zero visible change. Once
   * the static frame is up, `tick()` early-returns. We reset this to `false` whenever reduced motion is
   * OFF, so flipping the OS setting off cleanly resumes animation and flipping it on re-composes once.
   */
  private staticComposed = false;

  constructor(private readonly app: Application) {
    const W = () => app.screen.width;
    const H = () => app.screen.height;

    // Sky: one full-bleed rect we TINT (no per-frame redraw). Drawn oversized + resized via scale so a
    // window resize never needs a geometry rebuild.
    this.sky = new Graphics().rect(0, 0, 1, 1).fill(0xffffff);
    this.root.addChild(this.sky);

    // Meadow: a soft rolling hill silhouette along the bottom, so the ducks have a place to glide over.
    this.meadow = new Graphics();
    this.root.addChild(this.meadow);

    // Ambient ducks: pooled, drawn once, only their transform changes per frame. Spread across colours
    // so the world feels populated and warm (the "a duck is present" first-frame delight).
    const palette: DuckColor[] = ['yellow', 'white', 'green', 'blue', 'pink'];
    for (let i = 0; i < AMBIENT_DUCKS; i++) {
      const color = palette[i % palette.length]!;
      const gfx = makeDuck(DUCK_HEX[color]);
      const scale = 0.6 + (i % 3) * 0.22;
      gfx.scale.set(scale);
      this.root.addChild(gfx);
      this.ducks.push({
        gfx,
        x: 0,
        y: 0,
        speed: 14 + (i % 4) * 8,
        dir: i % 2 === 0 ? 1 : -1,
        bobPhase: i * 1.3,
        bobAmp: 3 + (i % 2) * 2,
        baseY: 0,
      });
    }

    // Floating motes: tiny soft dots that drift up through the light. Pooled circles, reused forever.
    for (let i = 0; i < MOTES; i++) {
      const gfx = new Graphics().circle(0, 0, 1.6 + (i % 3) * 0.7).fill({ color: 0xffffff, alpha: 0.5 });
      this.root.addChild(gfx);
      this.motes.push({ gfx, x: 0, y: 0, vx: 0, vy: 0, phase: i * 0.7 });
    }

    this.app.stage.addChild(this.root);
    this.layout(W(), H()); // place everything for the first frame (so frame #1 is already composed)
  }

  /** (Re)place pooled actors for the current canvas size. Cheap; called on first frame + each tick. */
  private layout(W: number, H: number): void {
    this.sky.scale.set(W, H);

    // Rolling meadow silhouette across the bottom ~22% of the screen.
    const top = H * 0.78;
    this.meadow
      .clear()
      .moveTo(0, H)
      .lineTo(0, top + 18)
      .bezierCurveTo(W * 0.25, top - 26, W * 0.5, top + 22, W * 0.72, top - 8)
      .bezierCurveTo(W * 0.86, top - 22, W * 0.95, top + 6, W, top)
      .lineTo(W, H)
      .closePath()
      .fill({ color: 0x2f6e44, alpha: 0.55 });

    for (let i = 0; i < this.ducks.length; i++) {
      const d = this.ducks[i]!;
      // Spread the ducks vertically across the lower-middle band so they read as "on the meadow".
      d.baseY = top - 8 - (i % 3) * 34;
      d.y = d.baseY;
      // Seed x positions only once (don't yank them around on resize).
      if (this.firstFrame) d.x = ((i + 0.5) / this.ducks.length) * W;
      else if (d.x > W + 60) d.x = -40; // gently pull strays back in after a shrink
    }

    for (let i = 0; i < this.motes.length; i++) {
      const m = this.motes[i]!;
      if (this.firstFrame) {
        m.x = ((i * 137.5) % 100) / 100 * W; // golden-angle scatter, no Math.random at construction
        m.y = ((i * 263.1) % 100) / 100 * H;
        m.vx = (i % 2 === 0 ? 1 : -1) * (3 + (i % 3));
        m.vy = -(6 + (i % 4) * 3);
      }
    }
  }

  /** Per-frame driver. Pure tint/transform math — zero allocations. */
  readonly tick = (): void => {
    const W = this.app.screen.width;
    const H = this.app.screen.height;
    const dt = Math.min(this.app.ticker.deltaMS, 50) / 1000;
    const reduced = isReducedMotion();

    // When motion is allowed, keep the static-frame latch cleared so flipping reduced-motion OFF
    // mid-session resumes animation immediately (and a later flip ON re-composes exactly once).
    if (!reduced) this.staticComposed = false;

    const resized =
      Math.abs(this.sky.scale.x - W) > 0.5 || Math.abs(this.sky.scale.y - H) > 0.5;

    // Reduced motion: compose ONE correct static frame, then stop per-frame work entirely (re-writing
    // identical props every tick would force a needless full-stage redraw). A resize invalidates the
    // composed frame so the static meadow re-fits the new canvas.
    if (reduced) {
      if (this.staticComposed && !resized) return;
      this.layout(W, H);
      this.updateSky(currentTimeOfDay(), true, dt);
      this.composeReducedFrame();
      this.firstFrame = false;
      this.staticComposed = true;
      return;
    }

    // Keep geometry correct on resize (and compose the very first frame).
    if (this.firstFrame || resized) {
      this.layout(W, H);
    }

    // ── Sky: a real-time-of-day wash. Day clock advances slowly so the sky visibly breathes. ──
    this.clock += dt * 0.004; // a gentle drift (~one full day every ~4 min of dwell)
    const t = (currentTimeOfDay() + this.clock) % 1;
    this.updateSky(t, false, dt);

    // ── Ambient ducks glide + bob. ──
    for (const d of this.ducks) {
      d.x += d.speed * d.dir * dt;
      if (d.x > W + 60) d.x = -60;
      else if (d.x < -60) d.x = W + 60;
      d.bobPhase += dt * 1.6;
      d.gfx.y = d.baseY + Math.sin(d.bobPhase) * d.bobAmp;
      d.gfx.x = d.x;
      // Face travel direction (mirror the body horizontally).
      d.gfx.scale.x = (d.dir === -1 ? -1 : 1) * Math.abs(d.gfx.scale.x);
    }

    // ── Motes drift up and wrap. ──
    for (const m of this.motes) {
      m.x += m.vx * dt;
      m.y += m.vy * dt;
      m.phase += dt * 2;
      if (m.y < -8) {
        m.y = H + 8;
        m.x = ((m.phase * 53.7) % W + W) % W;
      }
      if (m.x < -8) m.x = W + 8;
      else if (m.x > W + 8) m.x = -8;
      m.gfx.alpha = 0.3 + (Math.sin(m.phase) + 1) * 0.2; // soft twinkle
      m.gfx.x = m.x;
      m.gfx.y = m.y;
    }

    this.firstFrame = false;
  };

  /**
   * Compose the single STATIC reduced-motion frame: ducks upright at their seeded positions, motes at a
   * calm fixed alpha. The sky was already pinned to `currentTimeOfDay()` by the caller. Called once when
   * reduced motion turns on (and again only on resize) — never on the per-frame hot path.
   */
  private composeReducedFrame(): void {
    for (const d of this.ducks) {
      d.gfx.x = d.x;
      d.gfx.y = d.baseY;
      d.gfx.scale.x = (d.dir === -1 ? -1 : 1) * Math.abs(d.gfx.scale.x);
    }
    for (const m of this.motes) {
      m.gfx.x = m.x;
      m.gfx.y = m.y;
      m.gfx.alpha = 0.4;
    }
  }

  private updateSky(t: number, reduced: boolean, dt: number): void {
    const phase = dayPhase(t);
    const raw = daylight(t);
    const light = MIN_DAYLIGHT + (1 - MIN_DAYLIGHT) * raw;

    let warmth: number; // 0 = cool/night, 1 = warm dawn/dusk
    if (phase === 'night') warmth = 0.05;
    else if (phase === 'dawn' || phase === 'dusk') warmth = 1;
    else warmth = 0.45;

    // Target wash colour, scaled by brightness so midday is bright and night is a deep, readable blue.
    const tr = (COOL.r + (WARM.r - COOL.r) * warmth) * light;
    const tg = (COOL.g + (WARM.g - COOL.g) * warmth) * light;
    const tb = (COOL.b + (WARM.b - COOL.b) * warmth) * light;

    const k = reduced || this.firstFrame ? 1 : Math.min(1, 1.5 * dt);
    this.light.r += (tr - this.light.r) * k;
    this.light.g += (tg - this.light.g) * k;
    this.light.b += (tb - this.light.b) * k;

    const r = Math.round(clamp01(this.light.r) * 255);
    const g = Math.round(clamp01(this.light.g) * 255);
    const b = Math.round(clamp01(this.light.b) * 255);
    this.sky.tint = (r << 16) | (g << 8) | b;
  }

  destroy(): void {
    this.ducks.length = 0;
    this.motes.length = 0;
  }
}

/** Build a small procedural duck (mirrors the office duck silhouette, simplified for a backdrop). */
function makeDuck(hex: number): Container {
  const c = new Container();
  // Soft shadow on the meadow.
  c.addChild(new Graphics().ellipse(0, 20, 16, 5).fill({ color: 0x000000, alpha: 0.18 }));
  // Body + belly.
  c.addChild(
    new Graphics()
      .ellipse(0, 0, 18, 14)
      .fill(hex)
      .ellipse(2, 3, 12, 9)
      .fill({ color: 0xffffff, alpha: 0.18 }),
  );
  // Head + eye + beak (facing right).
  c.addChild(
    new Graphics()
      .circle(11, -11, 9)
      .fill(hex)
      .circle(14, -13, 1.9)
      .fill(0x141821)
      .poly([19, -11, 27, -8.5, 19, -6])
      .fill(BEAK),
  );
  return c;
}

/** Wall-clock time-of-day in [0,1): 0 = midnight, 0.5 = noon. Pure; safe to call every frame. */
function currentTimeOfDay(): number {
  const now = new Date();
  return (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) / 86400;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
